import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ShoppingCart, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardFooter } from '../components/ui/card';
import { useCart } from '../context/CartContext';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast({
        title: 'Error',
        description: 'Failed to load products',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product);
    toast({
      title: 'Added to cart',
      description: `${product.name} has been added to your cart`,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white">
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-rose-100/50 to-transparent"></div>
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <img
            src="https://customer-assets.emergentagent.com/job_studio-builder-12/artifacts/72mg2eao_%40theelenistudio.png"
            alt="Eleni Studio"
            className="w-48 h-48 mx-auto mb-8 animate-in fade-in zoom-in duration-700"
          />
          <h1 className="text-5xl md:text-7xl font-serif text-rose-900 mb-6 animate-in slide-in-from-bottom duration-700">
            Handcrafted Jewelry
          </h1>
          <p className="text-xl md:text-2xl text-rose-700 mb-8 font-light animate-in slide-in-from-bottom duration-700 delay-150">
            Each piece crafted with love, just for you
          </p>
          <div className="flex gap-4 justify-center items-center animate-in slide-in-from-bottom duration-700 delay-300">
            <a
              href="https://instagram.com/theelenistudio"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-rose-800 hover:text-rose-600 transition-colors"
            >
              <Instagram className="w-5 h-5" />
              @theelenistudio
            </a>
            <span className="text-rose-300">|</span>
            <a
              href="https://wa.me/919513556777"
              target="_blank"
              rel="noopener noreferrer"
              className="text-rose-800 hover:text-rose-600 transition-colors"
            >
              +91 9513556777
            </a>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-4xl font-serif text-rose-900 mb-12 text-center">
          Our Collection
        </h2>
        
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {[...Array(8)].map((_, i) => (
              <Card key={i} className="overflow-hidden animate-pulse">
                <div className="aspect-square bg-rose-100"></div>
                <CardContent className="p-6">
                  <div className="h-6 bg-rose-100 rounded mb-3"></div>
                  <div className="h-4 bg-rose-50 rounded"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-rose-600 text-lg">No products available yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {products.map((product) => (
              <Card
                key={product._id}
                className="group overflow-hidden border-rose-100 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 flex flex-col h-full"
              >
                <Link to={`/product/${product._id}`}>
                  <div className="aspect-square overflow-hidden bg-white">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                </Link>
                <CardContent className="p-6 flex-grow flex flex-col">
                  <Link to={`/product/${product._id}`} className="flex-grow">
                    <h3 className="text-lg font-medium text-rose-900 mb-2 group-hover:text-rose-700 transition-colors line-clamp-2 min-h-[3.5rem]">
                      {product.name}
                    </h3>
                  </Link>
                  <p className="text-2xl font-serif text-rose-800 mb-0">₹{product.price}</p>
                </CardContent>
                <CardFooter className="p-6 pt-0">
                  <Button
                    onClick={() => handleAddToCart(product)}
                    className="w-full bg-rose-800 hover:bg-rose-900 text-white transition-colors"
                  >
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Add to Cart
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* About Section */}
      <section className="bg-gradient-to-br from-rose-50 to-rose-100 py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-serif text-rose-900 mb-6">
            About Eleni Studio
          </h2>
          <p className="text-lg text-rose-800 leading-relaxed">
            At Eleni Studio, we believe in the power of handcrafted jewelry to tell your unique story. 
            Each piece is carefully designed and crafted with attention to detail, using quality materials 
            and traditional techniques. From elegant earrings to stunning necklace sets, our collection 
            celebrates individuality and artisanal craftsmanship.
          </p>
        </div>
      </section>
    </div>
  );
};

export default Home;
